import { QueryFactory } from '@lmt/shared-types';
import { OptionalSubtenantDictionaryRecordDto } from '@lmt/shared-types';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { sortingQueryParams } from '@lmt/unicorn-shared/app/components/common/table/operations';
import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SearchInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/search/SearchInput';
import { useInsertKeyHandler } from '@lmt/unicorn-shared/app/components/dictionaries/useInsertKeyHandler';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { SubtenantsFilter } from '@lmt/unicorn-shared/app/components/patients/list/components/SubtenantsFilter';
import { useSelectedSubtenants } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameof } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import { snakeCase } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import {
  NumericArrayParam,
  StringParam,
  withDefault,
} from 'react-router-url-params';
import { useDebouncedCallback } from 'use-debounce';
import { DictionaryInfo } from '../dictionaries.types';
import { DictionaryRecordsTable } from './components/DictionaryRecordsTable';

import tableStyles from '@lmt/unicorn-shared/app/components/common/table/TableTitle.module.scss';
import { useModal } from '../../common/ui-kit/modal/useModal';
import { useEventCallback } from '@mui/material';

export interface DictionaryListProps {
  dictionary: DictionaryInfo;
}

export const DictionaryRecordList: FC<DictionaryListProps> = (props) => {
  const queryClient = useQueryClient();

  const query = props.dictionary.getData();
  const data = query.data ?? [];
  const [loadingTitle, setLoadingTitle] = useState<string | undefined>(
    undefined,
  );
  const [parsedQueryString, setQueryParams] = useQueryParamsInTab({
    search: withDefault(StringParam, undefined),
    subtenants: withDefault(NumericArrayParam, [] as number[]),
    ...sortingQueryParams(
      nameof<OptionalSubtenantDictionaryRecordDto>('value'),
      false,
    ),
  });

  const i18next = useScopedTranslation('dictionaries', NS_DataBlockFields);
  const dispatch = useDispatch();
  const modals = useModal();
  const forms = useGlobalForms();

  const selectedSubtenants = useSelectedSubtenants(
    parsedQueryString.subtenants,
  );

  const result = useMemo(() => {
    const activeData = data.filter((record) => !record.isDeleted);
    const subtenantIds = parsedQueryString.subtenants;

    let filteredData = props.dictionary.isOptionalSubtenant
      ? activeData.filter(
          (record) =>
            !record.subtenantId ||
            !subtenantIds ||
            subtenantIds.length === 0 ||
            subtenantIds?.includes(record.subtenantId),
        )
      : activeData;

    if (parsedQueryString.search) {
      filteredData = filteredData.filter((x) =>
        x.value.toLowerCase().includes(parsedQueryString.search!.toLowerCase()),
      );
    }

    return {
      dataList: filteredData,
      totalCount: filteredData.length,
    };
  }, [data, ...Object.values(parsedQueryString)]);

  const openCreate = useEventCallback(() =>
    forms.openDictionaryRecordCreating(props.dictionary),
  );

  const openEdit = useEventCallback((id: number) => {
    const record = data.find((record) => record.id === id);
    if (record) {
      forms.openDictionaryRecordEditing(props.dictionary, record);
    }
  });

  const removeDictionaryRecord = useEventCallback(async (recordId: number) => {
    const isConfirmed = await modals.showConfirm({
      text: i18next.t('list.confirm_message_remove'),
    });
    if (isConfirmed) {
      try {
        setLoadingTitle(i18next.t('list.loading_delete'));

        await props.dictionary.remove(recordId);
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(),
        });
        if (props.dictionary.invalidate) {
          await props.dictionary.invalidate(queryClient);
        }
      } catch (error) {
        await modals.showError({
          text: i18next.t('list.error_message_remove'),
        });
      } finally {
        setLoadingTitle(undefined);
      }
    }
  });

  const removeUnusedDictionaryRecords = useEventCallback(async () => {
    if (!props.dictionary.removeUnused) {
      return;
    }
    const isConfirmed = await modals.showConfirm({
      text: i18next.t('list.confirm_message_remove_unused_records'),
    });
    if (isConfirmed) {
      try {
        setLoadingTitle(i18next.t('list.loading_delete'));

        await props.dictionary.removeUnused();
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(),
        });
        if (props.dictionary.invalidate) {
          await props.dictionary.invalidate(queryClient);
        }
      } catch (error) {
        await modals.showError({
          text: i18next.t('list.error_message_remove_unused'),
        });
      } finally {
        setLoadingTitle(undefined);
      }
    }
  });

  const onSearchUpdated = useDebouncedCallback((value: string) => {
    setQueryParams({
      search: value,
    });
  }, 300);

  useInsertKeyHandler(openCreate);

  return (
    <div
      className={tableStyles.pageContainer}
      data-test-id="table-page-container"
    >
      <div className={tableStyles.tableToolbar}>
        <TableTitle
          title={i18next.t(`${snakeCase(props.dictionary.name)}.title` as any)}
          count={result.totalCount}
        />
        <div className={tableStyles.searchField}>
          <SearchInput
            placeholder={i18next.t('list.search_placeholder')}
            bordered={true}
            onChange={(e) => onSearchUpdated(e.target.value)}
            onSearchIconClick={onSearchUpdated}
            onClear={onSearchUpdated}
            initialValue={parsedQueryString.search}
          />
        </div>
        <div className={tableStyles.filters}>
          {props.dictionary.isOptionalSubtenant && (
            <SubtenantsFilter
              selectedSubtenants={selectedSubtenants}
              changeFilter={(subtenants) => {
                setQueryParams({
                  subtenants: subtenants.map((x) => x.id),
                });
              }}
            />
          )}
          {!!props.dictionary.removeUnused && (
            <Button
              title={
                i18next.t(
                  `${snakeCase(props.dictionary.name)}.remove_unused` as any,
                ) as string
              }
              color={ButtonColor.Danger}
              onClick={removeUnusedDictionaryRecords}
              className={tableStyles.filterItem}
            />
          )}
          <Button
            title={
              i18next.t(
                `${snakeCase(props.dictionary.name)}.add_button` as any,
              ) as string
            }
            color={ButtonColor.Primary}
            onClick={openCreate}
            iconComponent={<AddIcon />}
            className={tableStyles.filterItem}
            testId={'plus-button'}
          />
        </div>
      </div>
      <div className={tableStyles.tableContainer}>
        <Loading
          loading={query.isLoading || !!loadingTitle}
          title={loadingTitle || i18next.t('list.loading')}
        >
          <DictionaryRecordsTable
            data={result.dataList}
            noResults={result.totalCount === 0 && !parsedQueryString.search}
            onDelete={removeDictionaryRecord}
            onEdit={openEdit}
            prefix={props.dictionary.prefix}
            sorting={parsedQueryString}
            changeSorting={setQueryParams}
          />
        </Loading>
      </div>
    </div>
  );
};
